import React, { useEffect, useState } from 'react';
import '../index.css';
import "./Home.css";
import StepsExplainer from '../components/StepsExplainer';
import { useCommunitiesContext } from '../hooks/useCommunitiesContext';
import { useAuthContext } from '../hooks/useAuthContext';
import LoggedInHome from './LoggedInHome';
import Slider from "react-slick";
import CommunityDetails from '../components/CommunityDetails';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

// Helper function to extract tags from a community
const getTagsArray = (community) => {
  if (!community.tags) return [];
  if (typeof community.tags === "string") {
    return community.tags.split(",").map(t => t.trim()).filter(Boolean);
  } else if (Array.isArray(community.tags)) {
    return community.tags.map(t => t.trim()).filter(Boolean);
  }
  return [];
};

// Define key interest tags that represent main group types
const MAIN_INTEREST_TAGS = [
  'Friends', 
  'Sport', 
  'Exercise', 
  'Fitness',
  'Beauty', 
  'Health',
  'Creative', 
  'Art',
  'Music', 
  'Film',
  'Food', 
  'Travel',
  'Business', 
  'Professional',
  'Gaming', 
  'Technology',
  'Science', 
  'Learning',
  'Volunteering', 
  'Community',
  'Parenting', 
  'Family',
  'Wellness', 
  'Mindfulness',
  'Outdoor', 
  'Adventure'
];

const Home = ({ setCommunityTag }) => {
  const { communities, dispatch } = useCommunitiesContext();
  const { user } = useAuthContext();
  const [availableTags, setAvailableTags] = useState([]);
  const [mainInterestTags, setMainInterestTags] = useState([]);
  const [backgroundImages, setBackgroundImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [needsTopGradient, setNeedsTopGradient] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  const PLACEHOLDER_IMG = "https://assets.streamlinehq.com/image/private/w_512,h_512,ar_1/f_auto/v1/icons/3/shooting-star-pnhn77eiumd9cca9iyw1b.png/shooting-star-ey0t57fhladsuoxiccij7.png?_a=DATAdtAAZAA0";
  
  // Function to analyze image brightness (from CommunityHome.js)
  const analyzeImageBrightness = (imageUrl) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        
        let totalBrightness = 0;
        let pixelCount = 0;
        let whitePixelCount = 0;
        
        // Analyze top portion for navbar area
        const topAreaHeight = Math.floor(canvas.height * 0.25);
        const isMobile = window.innerWidth <= 768;
        const startY = 0;
        const endY = topAreaHeight;
        
        for (let y = startY; y < endY; y++) {
          for (let x = 0; x < canvas.width; x++) {
            const index = (y * canvas.width + x) * 4;
            const r = data[index];
            const g = data[index + 1];
            const b = data[index + 2];
            const brightness = (r * 0.299 + g * 0.587 + b * 0.114);
            
            totalBrightness += brightness;
            pixelCount++;
            
            if (brightness > 240) {
              whitePixelCount++;
            }
          }
        }
        
        const avgBrightness = totalBrightness / pixelCount;
        const whitePixelRatio = whitePixelCount / pixelCount;
        
        // More sensitive threshold for gradient overlay
        const needsGradient = avgBrightness > 180 || whitePixelRatio > 0.3;
        setNeedsTopGradient(needsGradient);
        
      } catch (error) {
        console.log('Canvas analysis failed, applying gradient overlay as fallback');
        setNeedsTopGradient(true);
      }
    };
    
    img.onerror = () => {
      console.log('Image load failed, applying gradient overlay as fallback');
      setNeedsTopGradient(true);
    };
    
    img.src = imageUrl;
  };
  

  // Fetch communities and extract tags and images
  useEffect(() => {
    const fetchCommunities = async () => {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/communities`);
      const json = await response.json();
      if (response.ok) {
        dispatch({ type: "SET_COMMUNITIES", payload: json });
        
        // Get all available tags from communities
        const allTags = new Set();
        json.forEach(c => {
          getTagsArray(c).forEach(tag => allTags.add(tag));
        });
        setAvailableTags(Array.from(allTags).sort());
        
        // Filter to show only main interest tags that actually exist in communities
        const existingMainTags = Array.from(allTags).filter(tag =>
          MAIN_INTEREST_TAGS.some(mainTag => 
            tag.toLowerCase().includes(mainTag.toLowerCase()) || 
            mainTag.toLowerCase().includes(tag.toLowerCase())
          )
        );
        setMainInterestTags(existingMainTags);
        
        // First images from the six featured communities
        const featuredCommunityNames = [
          'digital nomads', 'traveller', 'london culture cats',
          'edinburgh fringers', 'uk bikers', 'runners of london'
        ];
        const imageValidationPromises = [];
        const filteredCommunities = json.filter(community => {
          const title = (community.title || '').toLowerCase();
          return featuredCommunityNames.some(name => title.includes(name));
        });
        
        filteredCommunities.forEach(community => {
          if (community.pics) {
            let firstImage = null;
            
            // Handle both array and string formats
            if (Array.isArray(community.pics)) {
              firstImage = community.pics[0];
            } else if (typeof community.pics === 'string' && community.pics.trim()) {
              firstImage = community.pics.split(',')[0]?.trim();
            }
            
            if (firstImage && firstImage.trim()) {
              const imageUrl = firstImage.trim();
              
              // Create promise to validate image
              const validationPromise = new Promise((resolve) => {
                const img = new Image();
                img.crossOrigin = "anonymous";
                
                img.onload = () => {
                  // Check if image is high enough resolution (minimum 400x300)
                  if (img.naturalWidth >= 400 && img.naturalHeight >= 300) {
                    resolve({
                      url: imageUrl,
                      communityName: community.title || 'Community',
                      communityId: community._id
                    });
                  } else {
                    console.log(`Rejected low-res image: ${imageUrl} (${img.naturalWidth}x${img.naturalHeight})`);
                    resolve(null);
                  }
                };
                
                img.onerror = () => {
                  console.log(`Rejected broken image: ${imageUrl}`);
                  resolve(null);
                };
                
                // Set timeout to avoid hanging on slow images
                setTimeout(() => {
                  console.log(`Rejected slow-loading image: ${imageUrl}`);
                  resolve(null);
                }, 5000);
                
                img.src = imageUrl;
              });
              
              imageValidationPromises.push(validationPromise);
            }
          }
        });
        
        // Wait for all image validations to complete
        Promise.all(imageValidationPromises).then(validatedImages => {
          const validImages = validatedImages.filter(img => img !== null);
          console.log(`Validated ${validImages.length} images out of ${imageValidationPromises.length} total`);
          
          // Shuffle all valid community images for cycling
          const shuffledImages = validImages.sort(() => 0.5 - Math.random());
          const finalImages = shuffledImages.length > 0 ? shuffledImages : [
            { url: PLACEHOLDER_IMG, communityName: 'Community', communityId: null }
          ];
          
          // Preload all images before setting them
          const preloadPromises = finalImages.map(imageObj => {
            return new Promise((resolve) => {
              const img = new Image();
              img.onload = () => resolve(imageObj);
              img.onerror = () => resolve(imageObj); // Still resolve to show something
              img.src = imageObj.url;
            });
          });
          
          Promise.all(preloadPromises).then(preloadedImages => {
            setBackgroundImages(preloadedImages);
            setImagesLoaded(true);
            setIsLoading(false);
          });
        }).catch(error => {
          console.error('Error loading images:', error);
          // Fallback to placeholder
          setBackgroundImages([{ url: PLACEHOLDER_IMG, communityName: 'Community', communityId: null }]);
          setImagesLoaded(true);
          setIsLoading(false);
        });
      }
    };
    fetchCommunities();
  }, [dispatch]);
  
  // Image cycling effect
  useEffect(() => {
    if (backgroundImages.length > 1) {
      const interval = setInterval(() => {
        setCurrentImageIndex(prev => (prev + 1) % backgroundImages.length);
      }, 5000); // Change every 5 seconds
      
      return () => clearInterval(interval);
    }
  }, [backgroundImages.length]);
  
  // Analyze brightness when image changes and update community tag
  useEffect(() => {
    if (backgroundImages.length > 0 && backgroundImages[currentImageIndex]) {
      analyzeImageBrightness(backgroundImages[currentImageIndex].url);
      // Update community tag via callback to App component
      if (setCommunityTag) {
        setCommunityTag(`#${backgroundImages[currentImageIndex].communityName}`);
      }
    }
  }, [currentImageIndex, backgroundImages, setCommunityTag]);

  // Clear community tag when component unmounts
  useEffect(() => {
    return () => {
      if (setCommunityTag) {
        setCommunityTag(null);
      }
    };
  }, [setCommunityTag]);

  // Handle tag click - navigate to group search with selected tag
  const handleTagClick = (tag) => {
    window.location.href = `/groupsearch?tag=${encodeURIComponent(tag)}`;
  };

  // If user is logged in, show the dashboard instead
  if (user) {
    return <LoggedInHome />;
  }

  // Show loading screen while images are being preloaded
  if (isLoading || !imagesLoaded) {
    return (
      <div className="homepage-loading">
        <div className="loading-content">
          <div className="loading-spinner"></div>
          <p>Loading communities...</p>
        </div>
      </div>
    );
  }

  // Sample communities for carousel (first 6)
  const featuredCommunities = communities.slice(0, 6);

  // Slider settings
  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 800,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    arrows: false,
    autoplaySpeed: 4000,
    cssEase: 'ease',
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          infinite: true,
          dots: true
        }
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1
        }
      }
    ]
  };


  return (
    <div className="homepage-root">
      {/* SECTION: Hero */}
      <section className="hero-section">
        {backgroundImages.map((image, index) => (
          <div
            key={image.communityId || index}
            className={`hero-background-image ${index === currentImageIndex ? 'active' : ''}`}
            style={{ backgroundImage: `url(${image.url})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}
          />
        ))}
        {needsTopGradient && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '40%', background: 'linear-gradient(180deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.3) 50%, transparent 100%)', zIndex: 2, pointerEvents: 'none' }} />
        )}
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '70%', background: 'linear-gradient(0deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.7) 30%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0.3) 70%, transparent 100%)', zIndex: 2, pointerEvents: 'none' }} />
        <div className="hero-content" style={{ position: 'relative', zIndex: 3 }}>
          <div className="hero-text">
            <div style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', top: '-40px', left: '-60px', right: '-60px', bottom: '-40px', background: 'radial-gradient(ellipse 60% 50% at center, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.4) 30%, rgba(0,0,0,0.2) 50%, rgba(0,0,0,0.1) 70%, rgba(0,0,0,0.03) 85%, rgba(0,0,0,0) 100%)', zIndex: -1 }} />
              <span className="matching-platform-home">Find, Connect & Match</span>
              <h3 style={{ color: 'white', textShadow: '2px 2px 4px rgba(0,0,0,0.8)', fontSize: '1.2rem', padding: '1.2rem' }}>For every community</h3>
            </div>
          </div>
          <div id="afterImgPositioning">
            <a href="/groupsearch" className="button-fancy">Get Started</a>
            <div className="discreteLink"><span><a href="/login">Login</a></span></div>
          </div>
        </div>
      </section>

      {/* SECTION: Featured Communities */}
      <section className="featured-communities-section">
        <div className="featured-communities-container">
          <h2 className="featured-communities-heading">Featured Communities</h2>
          <p className="featured-communities-subtitle">Get introduced with people who share your interests</p>
          {featuredCommunities.length > 0 && (
            <Slider {...sliderSettings}>
              {featuredCommunities.map((community) => (
                <div key={community._id} className="featured-community-slide">
                  <CommunityDetails
                    community={community}
                    onTagClick={handleTagClick}
                  />
                </div>
              ))}
            </Slider>
          )}
        </div>
      </section>

{/* SECTION: How It Works */}
      <section id="how-it-works-section" className="how-it-works-section">
        <h2 className="how-it-works-heading">How it works</h2>
        <StepsExplainer />
      </section>

      {/* SECTION: Testimonials */}
      <section className="home-testimonials-section">
        <div className="home-testimonials-inner">
          <h2 className="home-testimonials-heading">What our members say</h2>
          <div className="home-testimonials-grid">
            {[
              { text: 'Friendly, professional and efficient to help us get things set up. Was a really easy process that matched me well with others.', author: 'Community Member' },
              { text: 'A really good service and helps find people in similar circumstances and hopefully lead to friendship. It was good to meet new people and share our stories.', author: 'Community Member' },
              { text: 'Really impressed with how quickly I was connected with like-minded people. The whole process felt natural and easy.', author: 'Community Member' }
            ].map((t, i) => (
              <div key={i} className="home-testimonial-card">
                <span className="home-testimonial-quote">"</span>
                <p className="home-testimonial-text">{t.text}</p>
                <p className="home-testimonial-author">— {t.author}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION: Community Statistics */}
      <section className="community-stats-section">
        <div className="community-stats-container">
          <h2 className="community-stats-heading">Join Our Growing Platform</h2>
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-number">{communities.length}+</div>
              <div className="stat-label">Active Communities</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">{availableTags.length}+</div>
              <div className="stat-label">Interest Categories</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">1000s</div>
              <div className="stat-label">Communities by Summer!</div>
            </div>
          </div>
        </div>
      </section>

      
      {/* New Section: Get introduced2 */}
{/* Combined Section: Get introduced */}
{/* 
<section id="getIntroducedContainer_home" className="banner-section seeking-dads">
  <div className="banner-grid_seeking-dads">
    <div className="title_seeking-dads" >
      <h2 className="banner-heading-top2"> Ask who else is about</h2> </div>

    <div className="banner-images-container_seeking-dads">
      <div className="banner-image-wrapper_seeking-dads">
        <img
          src="https://storage.googleapis.com/inter-public-website-assets/homepage-infograph-newdads-v1.png"
          alt="Introduction Part 1"
          className="hero-banner-image_seeking-dads"
        />
      </div> */}
      {/* <div className="banner-image-wrapper_seeking-dads">
        <img
          src="https://storage.googleapis.com/inter-public-website-assets/homepage-infograph-newdads_pt2-v1.png"
          alt="Introduction Part 2"
          className="hero-banner-image_seeking-dads"
        />
      </div> */}
    {/* </div>
  </div>
</section> */}






    </div>
  );
};


export default Home;
// import { Link } from 'react-router-dom';
// import '../index.css';
// import "./Home.css";
// import StepsExplainer from '../components/StepsExplainer';

// const Home = () => (
//   <div className="homepage-root">
//     {/* Hero section: always above the fold */}
//     <div className="hero-section">
//       <div className="hero-content">
//         <div className="hero-text">
//           <span className="matching-platform">A MATCHING PLATFORM</span>
//           <span className="for-anything">FOR ANYTHING</span>
//           <span className="but-dating">- but dating..</span>
//         </div>
//         <div id="afterImgPositioning">
//           <h3>
//             Find friends, running partners, other parents, ravers, or any other type of person!
//           </h3>
//           <div className="subtleLink">
//             <span>
//               <Link to="/moreinfo">More Info</Link>
//             </span>
//           </div>
//           <Link to="/groupsearch" className="button-fancy">
//             Get Started
//           </Link>
//           <div className="discreteLink">
//             <span>
//               <Link to="/login">Login</Link>
//             </span>
//           </div>
//         </div>
//       </div>
//     </div>
 
//     {/* Banner: below the fold */}
//     <section id="bannerContainer_home" className="banner-section get-introduced-section3">
//       <div className="banner-grid">
//         <div className="banner-top-left">
//           <h2 className="banner-heading-top">Join a group</h2>
//         </div>
//         <div className="banner-image-center">
//           {/* Desktop banner */}
//           <img
//             src="https://storage.googleapis.com/inter-public-website-assets/homepage-banner-landscape-4x8-v3.png"
//             alt="Introduction"
//             className="hero-banner-image hero-banner-image-desktop"
//           />
//           {/* Mobile banner */}
//           <img
//             src="https://storage.googleapis.com/inter-public-website-assets/homepage-banner-5x5-v6.png"
//             alt="Introduction"
//             className="hero-banner-image hero-banner-image-mobile"
//           />
//         </div>
//       </div>
//     </section>

// {/* New Section: Get introduced */}
// <section id="getIntroducedContainer_home" className="banner-section get-introduced-section">
//   <div className="banner-grid">
//     <div className="banner-top-left">
//       <h2 className="banner-heading-top">Get introduced</h2>
//     </div>
//     <div className="banner-image-center">
//       {/* Desktop banner */}
//       <img
//         src="https://storage.googleapis.com/inter-public-website-assets/homepage-infograph-runners-v2.png"
//         alt="Introduction"
//         className="hero-banner-image hero-banner-image-desktop"
//       />
//       {/* Mobile banner */}
//       <img
//         src="https://storage.googleapis.com/inter-public-website-assets/homepage-infograph-runners-v2.png"
//         alt="Introduction"
//         className="hero-banner-image hero-banner-image-mobile"
//       />
//     </div>
//   </div>
// </section>


//     {/* StepsExplainer: third step */}
//     <section className="how-it-works-section">
//       <h2 className="how-it-works-heading">How it works</h2>
//       <StepsExplainer />
//     </section>

// {/* New Section: Get introduced2 */}
// <section id="getIntroducedContainer_home" className="banner-section get-introduced-section2">
//   <div className="banner-grid">
//     <div className="banner-top-left">
//       <h2 className="banner-heading-top2">Set your purpose</h2>
//     </div>
//     <div className="banner-image-center">
//       {/* Desktop banner */}
//       <img
//         src="https://storage.googleapis.com/inter-public-website-assets/homepage-infograph-studybudies-v1.png"
//         alt="Introduction"
//         className="hero-banner-image hero-banner-image-desktop"
//       />
//       {/* Mobile banner */}
//       <img
//         src="https://storage.googleapis.com/inter-public-website-assets/homepage-infograph-studybudies-v1.png"
//         alt="Introduction"
//         className="hero-banner-image hero-banner-image-mobile"
//       />
//     </div>
//   </div>
// </section>



// {/* New Section: Get introduced2 */}
// <section id="getIntroducedContainer_home" className="banner-section seeking-dads">
//   <div className="banner-grid">
//     <div className="banner-top-left">
//       <h2 className="banner-heading-top2">Ask who else is about</h2>
//     </div>
//     <div className="banner-image-center">
//       {/* Desktop banner */}
//       <img
//         src="https://storage.googleapis.com/inter-public-website-assets/homepage-infograph-newdads-v1.png"
//         alt="Introduction"
//         className="hero-banner-image hero-banner-image-desktop"
//       />
//       {/* Mobile banner */}
//       <img
//         src="https://storage.googleapis.com/inter-public-website-assets/homepage-infograph-newdads-v1.png"
//         alt="Introduction"
//         className="hero-banner-image hero-banner-image-mobile"
//       />
//     </div>
//   </div>
// </section>



//   </div>

  
// );

// export default Home;
